import { useEffect, useState } from 'react';
import { Form, Input, Button, Tag } from 'antd';
import {
  CheckCircleTwoTone,
  PhoneOutlined,
  MailOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { getSignupState, patchSignupState } from '../../contexts/signupStore';
import { sanitizeOtp } from '../../utils/sanitize';
import { digitsOnly } from '../../utils/validators';
import { notifyError, notifySuccess } from '../../utils/notify';

export default function SignupOtpPage() {
  const navigate = useNavigate();
  const [mobileVerified, setMobileVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [submittingMobile, setSubmittingMobile] = useState(false);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  // Stage 5N-12: when the customer is SEZ AND the admin payment-approval
  // is still pending or has been rejected, redirect to the SEZ pending
  // screen instead of the payment step. Server-driven via getStatus's
  // currentStep field — frontend never decides this on its own.
  const [nextStep, setNextStep] = useState('payment');

  const state = getSignupState();
  const pendingSignupId = state.pendingSignupId;

  useEffect(() => {
    if (!pendingSignupId) navigate('/signup', { replace: true });
  }, [pendingSignupId, navigate]);

  if (!pendingSignupId) return null;

  async function refreshStatus() {
    try {
      const status = await endpoints.signup.status(pendingSignupId);
      const m = Boolean(status && status.mobile && status.mobile.verified);
      const e = Boolean(status && status.email && status.email.verified);
      setMobileVerified(m);
      setEmailVerified(e);
      // Stage 5N-12: pick up the server-resolved currentStep.
      if (status && status.currentStep) {
        setNextStep(status.currentStep);
      }
      patchSignupState({ status });
      return { m, e };
    } catch (_err) {
      return { m: false, e: false };
    }
  }

  useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitMobile(values) {
    setSubmittingMobile(true);
    try {
      const r = await endpoints.signup.verifyMobileOtp({
        pendingSignupId,
        otp: sanitizeOtp(values.mobileOtp)
      });
      setMobileVerified(Boolean(r.mobileVerified));
      setEmailVerified(Boolean(r.emailVerified));
      patchSignupState({ status: r });
      if (r.mobileVerified) {
        notifySuccess('Mobile OTP verified', 'You can now verify your email OTP.');
        if (r.emailVerified) refreshStatus();
      }
    } catch (err) {
      notifyError(err, { title: 'Mobile OTP verification failed' });
    } finally {
      setSubmittingMobile(false);
    }
  }

  async function submitEmail(values) {
    setSubmittingEmail(true);
    try {
      const r = await endpoints.signup.verifyEmailOtp({
        pendingSignupId,
        otp: sanitizeOtp(values.emailOtp)
      });
      setMobileVerified(Boolean(r.mobileVerified));
      setEmailVerified(Boolean(r.emailVerified));
      patchSignupState({ status: r });
      if (r.emailVerified) {
        notifySuccess(
          'Email OTP verified',
          r.mobileVerified
            ? 'Both channels are verified. You can continue to payment.'
            : 'You can now verify your mobile OTP.'
        );
        // Refresh server status so SEZ customers learn their next step
        // is sez_pending_approval rather than payment.
        if (r.mobileVerified) refreshStatus();
      }
    } catch (err) {
      notifyError(err, { title: 'Email OTP verification failed' });
    } finally {
      setSubmittingEmail(false);
    }
  }

  const bothVerified = mobileVerified && emailVerified;

  return (
    <div className="gi-onboarding-rise bg-white rounded-2xl shadow-elevated border border-surface-200 p-5 sm:p-7 lg:p-8">
      <div className="mt-2 mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0">
          Verify OTPs
        </h2>
        <p className="text-sm text-ink-500 mt-1.5 mb-0">
          Mock provider in dev — submit{' '}
          <code className="text-xs">000000</code> for both channels.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-surface-200 bg-surface-50 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-ink-900 font-semibold">
              <PhoneOutlined className="text-brand-500" /> Mobile OTP
            </div>
            {mobileVerified ? (
              <Tag
                color="success"
                icon={<CheckCircleTwoTone twoToneColor="#16a34a" />}
              >
                verified
              </Tag>
            ) : (
              <Tag>pending</Tag>
            )}
          </div>
          <Form layout="vertical" onFinish={submitMobile} disabled={mobileVerified}>
            <Form.Item
              label={<span className="text-sm font-medium text-ink-700">OTP code</span>}
              name="mobileOtp"
              getValueFromEvent={digitsOnly(6)}
              rules={[{ required: true, message: 'OTP is required' }]}
            >
              <Input
                maxLength={6}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingMobile}
              disabled={mobileVerified}
              block
            >
              Verify mobile
            </Button>
          </Form>
        </div>

        <div className="rounded-xl border border-surface-200 bg-surface-50 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-ink-900 font-semibold">
              <MailOutlined className="text-brand-500" /> Email OTP
            </div>
            {emailVerified ? (
              <Tag
                color="success"
                icon={<CheckCircleTwoTone twoToneColor="#16a34a" />}
              >
                verified
              </Tag>
            ) : (
              <Tag>pending</Tag>
            )}
          </div>
          <Form layout="vertical" onFinish={submitEmail} disabled={emailVerified}>
            <Form.Item
              label={<span className="text-sm font-medium text-ink-700">OTP code</span>}
              name="emailOtp"
              getValueFromEvent={digitsOnly(6)}
              rules={[{ required: true, message: 'OTP is required' }]}
            >
              <Input
                maxLength={6}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingEmail}
              disabled={emailVerified}
              block
            >
              Verify email
            </Button>
          </Form>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-surface-200 flex items-center justify-between flex-wrap gap-3">
        <Button onClick={() => navigate('/signup')} icon={<ArrowLeftOutlined />}>
          Back
        </Button>
        <Button
          type="primary"
          disabled={!bothVerified}
          onClick={() => {
            // Stage 5N-12: SEZ customers route through the pending-
            // approval screen instead of the payment step until the
            // admin commits a tax decision. nextStep comes from the
            // server-side currentStep field on /signup/status.
            const target =
              nextStep === 'sez_pending_approval'
                ? '/signup/sez-pending'
                : '/signup/payment';
            navigate(target);
          }}
          icon={<ArrowRightOutlined />}
          iconPosition="end"
        >
          {nextStep === 'sez_pending_approval'
            ? 'Continue (SEZ approval pending)'
            : 'Continue to payment'}
        </Button>
      </div>

      {!bothVerified ? (
        <p className="text-xs text-ink-500 mt-3 mb-0">
          Both channels must be verified before continuing.
        </p>
      ) : null}
      {bothVerified && nextStep === 'sez_pending_approval' ? (
        <p className="text-xs text-ink-500 mt-3 mb-0">
          Your SEZ registration is pending internal approval. Continue
          to view status.
        </p>
      ) : null}
    </div>
  );
}
